import { Box, Container, Grid, Skeleton, Stack, styled, Typography, useTheme } from '@mui/material';

//Components
import SpecialPlanCard from 'src/features/Pricing/SpecialPlanCard';

// Utilities
import { useQueryCampaignDetail } from 'src/services/queries';
import ImageComponent from 'src/components/common/Image';
import { ICON_GIFT_CAMPAIGN, ICON_PROMOTION_CAMPAIGN, ICON_WARNING_ACTION } from 'src/utils/constants/imagesUrl';
import { get, map } from 'lodash';
import { useParams } from 'react-router-dom';
import { ERROR_CODE } from 'src/utils/constants/statusCode';
import { KeyboardArrowLeft } from '@mui/icons-material';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import { CAMPAIGN_PROGRAM_KEY, DEFAULT_COOKIE_DURATION } from 'src/utils/constants/config';
import { setCookie } from 'src/utils/helpers/cookies';
import { useEffect } from 'react';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const Banner = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  background: ' linear-gradient(91deg, #422AFB 2.03%, #5451F8 98.7%)',
  borderRadius: theme.shape.borderRadius * 2,
  zIndex: 1000,
  position: 'relative',
  padding: theme.spacing(3),
  paddingLeft: theme.spacing(20),
  paddingRight: theme.spacing(20),
  gap: theme.spacing(4),
}));

const CampaignProgram = () => {
  const theme = useTheme();
  const { campaign_slug = '' } = useParams();
  const { data, isFetching } = useQueryCampaignDetail({ campaign_slug });
  const campaignDetail = data?.data;

  useEffect(() => {
    if (campaign_slug) {
      setCookie(CAMPAIGN_PROGRAM_KEY, campaign_slug, DEFAULT_COOKIE_DURATION);
    }
  }, []);

  if (isFetching) {
    return (
      <Box minHeight={'100vh'} width={'100%'} bgcolor={theme.palette.background.paper1}>
        <StyledContainer maxWidth="xl">
          <Box
            sx={{
              height: '100%',
              background: theme.palette.background.paper1,
            }}
          >
            <Skeleton variant="rectangular" animation="wave" height={150} />
            <Grid container spacing={3} pt={3}>
              {new Array(12).fill(0).map((_, index) => (
                <Grid key={`plan-detail-${index}-loading`} item sm={12} md={6} lg={4} xl={3}>
                  <Skeleton variant="rectangular" animation="wave" height={150} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </StyledContainer>
      </Box>
    );
  }

  if (data?.status_code === ERROR_CODE.NOT_FOUND) {
    return (
      <Box minHeight={'100vh'} width={'100%'} bgcolor={theme.palette.background.paper1}>
        <StyledContainer maxWidth="xl">
          <Box
            sx={{
              height: '100%',
              background: theme.palette.background.paper1,
            }}
          >
            <Banner>
              <ImageComponent imgSrc={ICON_WARNING_ACTION} height={150} />
              <Stack flex={2} justifyContent="center" gap={2} alignItems="center">
                <Typography variant="h3" color="white" align="left">
                  The campaign has ended.
                </Typography>
                <Typography variant="body1" color="white" align="left">
                  Please return to the main page to explore more interesting features
                </Typography>
                <RouterLinkNoUnderline to={AuthRouteKeys.Home} color="#fff">
                  <KeyboardArrowLeft color="inherit" />
                  Back to home
                </RouterLinkNoUnderline>
              </Stack>
            </Banner>
          </Box>
        </StyledContainer>
      </Box>
    );
  }

  return (
    <Box minHeight={'100vh'} width={'100%'} bgcolor={theme.palette.background.paper1}>
      <StyledContainer maxWidth="xl">
        <Box
          sx={{
            height: '100%',
            background: theme.palette.background.paper1,
          }}
        >
          <Banner>
            <ImageComponent
              imgSrc={ICON_PROMOTION_CAMPAIGN}
              style={{ position: 'absolute', top: 0, left: 0 }}
              height={150}
            />
            <ImageComponent
              imgSrc={ICON_GIFT_CAMPAIGN}
              style={{ position: 'absolute', bottom: 0, right: 0 }}
              height={150}
            />
            <Stack direction="row">
              <Stack flex={2} justifyContent="center" gap={2}>
                <Typography variant="h3" color="white" align="left">
                  {get(campaignDetail, 'name', '')}
                </Typography>
                <Typography variant="body1" color="white" align="left">
                  {get(campaignDetail, 'description', '')}
                </Typography>
                <Typography variant="h6" color="white" align="left">
                  {get(campaignDetail, 'call_to_action', '')}
                </Typography>
              </Stack>
            </Stack>
          </Banner>
          <Grid container spacing={3} pt={3}>
            {map(campaignDetail.plans, (planDetail, index) => (
              <Grid key={`plan-detail-${index}-${planDetail.id}`} item sm={12} md={6} lg={4} xl={3}>
                <SpecialPlanCard planDetail={planDetail} useInPublicPage={true} campaignSlug={campaign_slug || ''} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </StyledContainer>
    </Box>
  );
};

export default CampaignProgram;
